import { useMemo, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/table';
import { useTerminals } from '@/hooks/use-terminals';
import {
  downloadTerminalLogExport,
  useTerminalLogSources,
  useTerminalLogs,
  type TerminalLogExportFormat,
  type TerminalLogQuery,
  type TerminalLogSources,
} from '@/hooks/use-terminal-logs';
import { useT } from '@/i18n/use-t';

const LEVELS = ['error', 'warn', 'info', 'debug'] as const;
const PAGE_SIZE = 50;
const DAY_MS = 24 * 60 * 60 * 1000;

type ExportNotice = 'done' | 'truncated' | 'failed' | 'busy';

/** HTTP status of a failed request, if the server answered. */
function httpStatusOf(err: unknown): number | undefined {
  if (typeof err !== 'object' || err === null || !('response' in err)) return undefined;
  const response = (err as { response?: { status?: unknown } }).response;
  return typeof response?.status === 'number' ? response.status : undefined;
}

/**
 * RFL-160 — in-UI viewer/search over terminal device logs. RL-511: a range
 * older than the Back Office window also reads the log history (Loki, server
 * side); the page says which source a row came from and how far back history
 * goes for this tenant.
 */
export function TerminalLogsPage() {
  const t = useT('admin_ui');
  const terminalsQuery = useTerminals();
  const sources = useTerminalLogSources();

  // Draft filter inputs, applied to the query on Search.
  const [terminalId, setTerminalId] = useState('');
  const [level, setLevel] = useState('');
  const [tag, setTag] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [search, setSearch] = useState('');
  const [applied, setApplied] = useState<TerminalLogQuery>({ page: 1, pageSize: PAGE_SIZE });
  // History pages are cursor-paged: the cursors of the pages before this one.
  const [previousCursors, setPreviousCursors] = useState<(string | undefined)[]>([]);

  const logs = useTerminalLogs(applied);
  const result = logs.data;

  // RL-512: export of the applied filters and date range.
  const [exportFormat, setExportFormat] = useState<TerminalLogExportFormat>('csv');
  const [exporting, setExporting] = useState(false);
  const [exportNotice, setExportNotice] = useState<ExportNotice | null>(null);
  const exportBounds = sources.data?.export;
  const exportTooWide =
    exportBounds !== undefined &&
    applied.from !== undefined &&
    (applied.to === undefined ? Date.now() : Date.parse(applied.to)) - Date.parse(applied.from) >
      exportBounds.maxRangeDays * DAY_MS;

  async function exportLogs(): Promise<void> {
    setExporting(true);
    setExportNotice(null);
    try {
      const out = await downloadTerminalLogExport(applied, exportFormat);
      setExportNotice(out.truncated ? 'truncated' : 'done');
    } catch (err) {
      // 429: this store already runs its maximum of downloads (RL-512).
      setExportNotice(httpStatusOf(err) === 429 ? 'busy' : 'failed');
    } finally {
      setExporting(false);
    }
  }

  const totalPages = useMemo(
    () =>
      result === undefined || result.mode !== 'recent'
        ? 1
        : Math.max(1, Math.ceil(result.total / result.pageSize)),
    [result],
  );

  function toIso(local: string): string | undefined {
    if (local === '') return undefined;
    const d = new Date(local);
    return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
  }

  function applyFilters(): void {
    setApplied({
      terminalId: terminalId || undefined,
      level: level || undefined,
      tag: tag.trim() || undefined,
      from: toIso(from),
      to: toIso(to),
      search: search.trim() || undefined,
      page: 1,
      pageSize: PAGE_SIZE,
    });
    setPreviousCursors([]);
  }

  function goToPage(next: number): void {
    setApplied((prev) => ({ ...prev, page: Math.min(Math.max(1, next), totalPages) }));
  }

  function olderHistoryPage(nextCursor: string): void {
    setPreviousCursors((prev) => [...prev, applied.cursor]);
    setApplied((prev) => ({ ...prev, cursor: nextCursor }));
  }

  function newerHistoryPage(): void {
    const back = previousCursors[previousCursors.length - 1];
    setPreviousCursors((prev) => prev.slice(0, -1));
    setApplied((prev) => ({ ...prev, cursor: back }));
  }

  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-2xl font-semibold">{t('terminals.logs.title', 'Terminal logs')}</h1>
        <p className="text-sm text-muted-foreground">
          {t(
            'terminals.logs.help',
            'Search device/app logs shipped from terminals — by terminal, level, subsystem tag, time window, and message text. Newest first.',
          )}
        </p>
        {sources.data !== undefined && (
          <p className="mt-1 text-sm text-muted-foreground" data-testid="terminal-logs-sources">
            <SourcesNote sources={sources.data} />
          </p>
        )}
      </header>

      {/* ---------- Filters ---------- */}
      <section className="grid grid-cols-1 gap-3 rounded-card border border-border bg-card p-4 sm:grid-cols-2 lg:grid-cols-3">
        <div className="space-y-1">
          <Label htmlFor="flt-terminal">{t('terminals.field.terminal', 'Terminal')}</Label>
          <Select
            id="flt-terminal"
            value={terminalId}
            onChange={(e) => setTerminalId(e.target.value)}
          >
            <option value="">{t('terminals.logs.allTerminals', 'All terminals')}</option>
            {(terminalsQuery.data?.terminals ?? []).map((terminal) => (
              <option key={terminal.id} value={terminal.id}>
                {terminal.code} — {terminal.displayName}
              </option>
            ))}
          </Select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="flt-level">{t('terminals.logs.level', 'Level')}</Label>
          <Select id="flt-level" value={level} onChange={(e) => setLevel(e.target.value)}>
            <option value="">{t('terminals.logs.allLevels', 'All levels')}</option>
            {LEVELS.map((l) => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </Select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="flt-tag">{t('terminals.logs.tag', 'Tag / subsystem')}</Label>
          <Input
            id="flt-tag"
            value={tag}
            onChange={(e) => setTag(e.target.value)}
            placeholder={t('terminals.logs.tagPlaceholder', 'fiscal, sync, ui.returns…')}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="flt-from">{t('terminals.field.from', 'From')}</Label>
          <Input
            id="flt-from"
            type="datetime-local"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="flt-to">{t('terminals.field.to', 'To')}</Label>
          <Input
            id="flt-to"
            type="datetime-local"
            value={to}
            onChange={(e) => setTo(e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="flt-search">
            {t('terminals.logs.messageContains', 'Message contains')}
          </Label>
          <Input
            id="flt-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') applyFilters();
            }}
            placeholder={t('terminals.logs.searchPlaceholder', 'free text…')}
          />
        </div>
        <div className="flex flex-wrap items-end gap-2 sm:col-span-2 lg:col-span-3">
          <Button onClick={applyFilters} disabled={logs.isFetching}>
            {logs.isFetching
              ? t('terminals.logs.searching', 'Searching…')
              : t('terminals.logs.search', 'Search')}
          </Button>
          <div className="ml-auto flex items-end gap-2">
            <div className="space-y-1">
              <Label htmlFor="export-format">
                {t('terminals.logs.exportFormat', 'File format')}
              </Label>
              <Select
                id="export-format"
                value={exportFormat}
                onChange={(e) => setExportFormat(e.target.value as TerminalLogExportFormat)}
              >
                <option value="csv">{t('terminals.logs.exportCsv', 'CSV (spreadsheet)')}</option>
                <option value="ndjson">
                  {t('terminals.logs.exportNdjson', 'NDJSON, gzipped')}
                </option>
              </Select>
            </div>
            <Button
              variant="outline"
              onClick={() => void exportLogs()}
              disabled={exporting || exportTooWide}
            >
              {exporting
                ? t('terminals.logs.exporting', 'Preparing file…')
                : t('terminals.logs.export', 'Download')}
            </Button>
          </div>
        </div>
        {exportBounds !== undefined && (
          <p
            className="text-xs text-muted-foreground sm:col-span-2 lg:col-span-3"
            data-testid="terminal-logs-export-note"
          >
            {exportTooWide
              ? t(
                  'terminals.logs.exportTooWide',
                  'A download covers at most {days} days. Narrow From and To, then search again.',
                ).replace('{days}', String(exportBounds.maxRangeDays))
              : t(
                  'terminals.logs.exportHelp',
                  'Download saves the searched results, newest first: up to {days} days and {rows} rows per file.',
                )
                  .replace('{days}', String(exportBounds.maxRangeDays))
                  .replace('{rows}', String(exportBounds.maxRows))}
          </p>
        )}
        {exportNotice === 'truncated' && (
          <p className="text-sm text-destructive sm:col-span-2 lg:col-span-3" role="status">
            {t(
              'terminals.logs.exportTruncated',
              'The file is incomplete: it stopped at the download limit. Its last row says where it stopped; set To to that time, or narrow the filters, and download again.',
            )}
          </p>
        )}
        {exportNotice === 'failed' && (
          <p className="text-sm text-destructive sm:col-span-2 lg:col-span-3" role="status">
            {t('terminals.logs.exportFailed', 'The download failed. Try again.')}
          </p>
        )}
        {exportNotice === 'busy' && (
          <p className="text-sm text-destructive sm:col-span-2 lg:col-span-3" role="status">
            {t(
              'terminals.logs.exportBusy',
              '{max} downloads are already running. Wait for one to finish, then download again.',
            ).replace('{max}', String(exportBounds?.maxConcurrent ?? 2))}
          </p>
        )}
      </section>

      {/* ---------- Results ---------- */}
      {logs.isError ? (
        <p className="text-sm text-destructive">
          {t('terminals.logs.failed', 'Failed to load logs.')}
        </p>
      ) : result === undefined ? (
        <p className="text-sm text-muted-foreground">{t('common.loading', 'Loading…')}</p>
      ) : (
        <>
          {result.mode === 'history' && result.historyUnavailable && (
            <p className="text-sm text-destructive" role="alert">
              {t(
                'terminals.logs.historyUnavailable',
                'Older entries could not be read from the log history right now. Try again with Next.',
              )}
            </p>
          )}
          {result.entries.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {t('terminals.logs.empty', 'No log entries match these filters.')}
            </p>
          ) : (
            <div className="overflow-x-auto rounded-card border border-border">
              <Table>
                <THead>
                  <TR>
                    <TH>{t('terminals.logs.colTime', 'Time')}</TH>
                    <TH>{t('terminals.field.terminal', 'Terminal')}</TH>
                    <TH>{t('terminals.logs.level', 'Level')}</TH>
                    <TH>{t('terminals.logs.colTag', 'Tag')}</TH>
                    <TH>{t('terminals.logs.colMessage', 'Message')}</TH>
                    {result.mode === 'history' && (
                      <TH>{t('terminals.logs.colSource', 'Source')}</TH>
                    )}
                  </TR>
                </THead>
                <TBody>
                  {result.entries.map((e) => (
                    <TR key={e.id}>
                      <TD className="whitespace-nowrap font-mono text-xs">
                        {new Date(e.occurredAt).toLocaleString()}
                      </TD>
                      <TD className="whitespace-nowrap font-mono text-xs">{e.terminalCode}</TD>
                      <TD>
                        <LevelBadge level={e.level} />
                      </TD>
                      <TD className="whitespace-nowrap text-xs text-muted-foreground">
                        {e.tag ?? '—'}
                      </TD>
                      <TD className="max-w-xl break-words text-sm">{e.message}</TD>
                      {result.mode === 'history' && (
                        <TD className="whitespace-nowrap text-xs text-muted-foreground">
                          {e.source === 'history'
                            ? t('terminals.logs.sourceHistory', 'Log history')
                            : t('terminals.logs.sourceRecent', 'Back Office')}
                        </TD>
                      )}
                    </TR>
                  ))}
                </TBody>
              </Table>
            </div>
          )}

          {/* ---------- Pagination ---------- */}
          {result.mode === 'recent' ? (
            result.entries.length > 0 && (
              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <span>
                  {t('terminals.logs.pageSummary', '{total} {unit} · page {page} of {pages}')
                    .replace('{total}', String(result.total))
                    .replace(
                      '{unit}',
                      result.total === 1
                        ? t('terminals.logs.entry', 'entry')
                        : t('terminals.logs.entries', 'entries'),
                    )
                    .replace('{page}', String(result.page))
                    .replace('{pages}', String(totalPages))}
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => goToPage(result.page - 1)}
                    disabled={result.page <= 1 || logs.isFetching}
                  >
                    {t('terminals.pagination.previous', 'Previous')}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => goToPage(result.page + 1)}
                    disabled={result.page >= totalPages || logs.isFetching}
                  >
                    {t('terminals.pagination.next', 'Next')}
                  </Button>
                </div>
              </div>
            )
          ) : (
            <div className="flex items-center justify-between gap-3 text-sm text-muted-foreground">
              <span>
                {t('terminals.logs.historyPage', 'Page {page}').replace(
                  '{page}',
                  String(previousCursors.length + 1),
                )}
                {result.nextCursor !== null &&
                  !result.historyUnavailable &&
                  result.entries.length < result.pageSize && (
                    <>
                      {' · '}
                      {t(
                        'terminals.logs.searchedBackTo',
                        'Searched back to {date}. Next searches further back.',
                      ).replace('{date}', new Date(result.searchedBackTo).toLocaleString())}
                    </>
                  )}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={newerHistoryPage}
                  disabled={previousCursors.length === 0 || logs.isFetching}
                >
                  {t('terminals.pagination.previous', 'Previous')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (result.nextCursor !== null) olderHistoryPage(result.nextCursor);
                  }}
                  disabled={result.nextCursor === null || logs.isFetching}
                >
                  {t('terminals.pagination.next', 'Next')}
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/** RL-511 — where rows come from and how far back this tenant's history goes. */
function SourcesNote({ sources }: { sources: TerminalLogSources }) {
  const t = useT('admin_ui');
  const days = String(sources.recent.days);
  const { history } = sources;
  if (!history.enabled) {
    return (
      <>
        {t(
          'terminals.logs.sourcesRecentOnly',
          'Back Office keeps the last {days} days of terminal logs.',
        ).replace('{days}', days)}
      </>
    );
  }
  if (!history.available) {
    return (
      <>
        {t(
          'terminals.logs.sourcesHistoryDown',
          'The last {days} days come from Back Office. The log history for older entries is unavailable right now.',
        ).replace('{days}', days)}
      </>
    );
  }
  if (history.retentionDays === null || history.from === null) {
    return (
      <>
        {t(
          'terminals.logs.sourcesHistoryOpen',
          'The last {days} days come from Back Office; set From earlier to search older entries in the log history.',
        ).replace('{days}', days)}
      </>
    );
  }
  return (
    <>
      {t(
        'terminals.logs.sourcesHistory',
        'The last {days} days come from Back Office. Older entries come from the log history, which keeps {historyDays} days (back to {date}). Set From earlier to search them.',
      )
        .replace('{days}', days)
        .replace('{historyDays}', String(history.retentionDays))
        .replace('{date}', new Date(history.from).toLocaleDateString())}
    </>
  );
}

function LevelBadge({ level }: { level: string }) {
  const l = level.toLowerCase();
  if (l === 'error' || l === 'fatal') return <Badge variant="destructive">{level}</Badge>;
  if (l === 'warn' || l === 'warning') return <Badge variant="outline">{level}</Badge>;
  return <Badge variant="default">{level}</Badge>;
}
